import type { ReactNode } from 'react';

import {
  Badge,
  Button,
  DataTableExpandToggle,
  type DataTableFeatures,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from 'design-system';
import { CopyPlusIcon, LockIcon } from 'lucide-react';

import type { CellContext } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { isRecentlyLaunched } from '@/entities/catalog-item';
import { DateTextVariants } from '@/features/_shared/user';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';
import { getDiffColor } from '@/shared/lib/format/getDiffColor';

import { ADDITIONAL_ITEM } from '../../constants';

const REQUEST_ACCESS_URL = 'https://www.general-index.com/contact-us';

export interface CatalogCellContentProps {
  context: CellContext<DataTableFeatures, CatalogItem>;
  formatDateString: (value: string, variant: DateTextVariants) => string;
  isInDashboard: boolean;
  isRestricted: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}

function TextWithTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <span className='block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'>
              {text}
            </span>
          }
        />
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function RestrictedValue() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              aria-label='Restricted value'
              className='inline-flex items-center text-muted-foreground'
            >
              <LockIcon />
            </span>
          }
        />
        <TooltipContent>
          <span>You don&apos;t have access to this.</span>{' '}
          <Button
            className='h-auto p-0 text-inherit'
            render={
              <a
                href={REQUEST_ACCESS_URL}
                rel='noopener noreferrer'
                target='_blank'
              />
            }
            variant='link'
          >
            Request access
          </Button>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function AdditionalItemIndicator() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              aria-label='Additional items available'
              className='inline-flex items-center'
            >
              <CopyPlusIcon />
            </span>
          }
        />
        <TooltipContent>
          This GX code has additional items. Check Display settings to view them.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function getCellText(value: unknown): string {
  switch (typeof value) {
    case 'string':
      return value;
    case 'number':
    case 'bigint':
      return value.toString();
    case 'boolean':
      return value ? 'true' : 'false';
    default:
      return '';
  }
}

export function CatalogCellContent({
  context,
  formatDateString,
  isInDashboard,
  isRestricted,
  renderFactsheetButton
}: CatalogCellContentProps) {
  const columnId = context.column.id;
  const value = context.getValue();
  const text = getCellText(value);

  switch (columnId) {
    case 'Title':
      return (
        <span className='flex min-w-0 items-center gap-1'>
          <DataTableExpandToggle row={context.row} />
          <span className='min-w-0 flex-1'>
            <TextWithTooltip text={text} />
          </span>
        </span>
      );
    case 'IndexModule':
    case 'Commodity':
      return <TextWithTooltip text={text.split(',').join(', ')} />;
    case 'LastDate':
    case 'StartDate':
    case 'EndDate':
      return formatDateString(text, DateTextVariants.FULL);
    case 'LaunchDate':
      return isRecentlyLaunched(text, new Date()) ? <Badge variant='new'>NEW</Badge> : null;
    case ADDITIONAL_ITEM:
      return context.row.original[ADDITIONAL_ITEM] ? <AdditionalItemIndicator /> : null;
    case 'Last':
      return isRestricted ? <RestrictedValue /> : formatNumberWithPrecision(text);
    case 'Change':
      return isRestricted ? (
        <RestrictedValue />
      ) : (
        <span style={{ color: getDiffColor(Number(text)) }}>{formatNumberWithPrecision(text)}</span>
      );
    case 'Factsheet':
      return renderFactsheetButton?.(context.row.original.Code, isInDashboard ? 'newTab' : 'modal');
    case 'Periods':
      return text.padStart(2, '0');
    case 'Index':
      return text === 'Y' ? 'Index' : 'High, Mid, Low';
    default:
      return text;
  }
}
